import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ComplianceEvidenceResult } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * The evidence pack (G-279): each control with its measured status and the
 * sentence that proves it, printable and downloadable for a questionnaire.
 */

const TONE: Record<string, string> = {
  met: 'text-success-text',
  partial: 'text-warning-text',
  'not-met': 'text-danger-text',
  hosting: 'text-ink-meta',
};

export function EvidencePanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = can('setup.audit.view');
  const pack = useQuery<ComplianceEvidenceResult, ApiError>({ queryKey: ['compliance-evidence'], queryFn: () => api.complianceEvidence({}), enabled });
  if (!enabled || !pack.data) return null;
  const data = pack.data;
  const download = (): void => {
    try {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      link.download = 'security-evidence.json';
      link.click();
    } catch {
      // Nowhere to save to here; the panel itself is the evidence.
    }
  };
  return (
    <Panel data-region="evidence">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('evidence.title')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('evidence.body', { values: { when: formatDateTime(data.generatedAt, format) } })}</p>
          </div>
          <span className="ms-auto flex gap-2">
            <Button intent="secondary" size="small" data-evidence-download onClick={download}>{t('evidence.download')}</Button>
            <Button intent="ghost" size="small" onClick={() => window.print()}>{t('evidence.print')}</Button>
          </span>
        </div>
        <ul className="flex flex-col">
          {data.controls.map((control) => (
            <li key={control.key} data-evidence-control={control.key} data-evidence-status={control.status} className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-body text-ink">{t(`evidence.control.${control.key}` as 'evidence.control.mfa')}</span>
                <span className={`text-meta font-medium ${TONE[control.status] ?? ''}`}>{t(`evidence.status.${control.status}` as 'evidence.status.met')}</span>
                <span className="ms-auto text-meta text-ink-meta">{control.frameworks.join(' · ')}</span>
              </div>
              <p className="text-dense text-ink-600" data-evidence-text>{control.evidence}</p>
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
